// ABOUTME: Relation "nooit verkochte producten" report — a TopRow of cards over a TabView3 grid, matching the
// ABOUTME: orders/shipments index pattern. Hosted in the relation StockHub BI (view-only, no dashboards yet).
import { Box, useTheme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { format, startOfMonth, endOfMonth } from "date-fns";
import { tokens } from "../../../../theme";
import TabView3 from "../../../../components/theme/TabView3/TabView3";
import ShDateRangePicker from "../../../../components/theme/ShDateRangePicker";
import Subheading from "../../../../components/theme/text/Subheading";
import { useTopbar } from "../../../../contexts/TopbarProvider";
import { useGetNeverSoldTotalsQuery } from "../../../../newapi/reports/relation/reportRelationNeverSoldSlice";
import NeverSoldCards from "./NeverSoldCards";
import NeverSold from "./views/NeverSold";
import { neverSoldConfig } from "./neverSoldConfig";

// The OverflowRow of cards — sits above the TabView3, like the orders/shipments TopRow.
const TopRow = ({ dates, filter, setFilter }) => {
    const { data: totals, isFetching } = useGetNeverSoldTotalsQuery({ startDate: dates.startDate, endDate: dates.endDate });
    return (
        <NeverSoldCards totals={totals} isLoading={isFetching} activeFilter={filter} onFilter={setFilter} />
    );
};

// The date range picker, mounted next to the page title in the Topbar via showExtraTitle. Hidden on phones,
// where MobileFilter renders it full width below the topbar instead (like the relation dashboard).
const TopBarFilter = ({ dates, setDates }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    return (
        <ShDateRangePicker
            startDate={dates.startDate}
            endDate={dates.endDate}
            darker
            size={32}
            onClose={(v) => setDates({ startDate: v[0].formattedStartDate, endDate: v[0].formattedEndDate })}
            sx={{
                background: `${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200]}`,
                border: `2px solid ${"transparent"}`,
                "&:hover": {
                    background: `${theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[100]}`,
                    border: `2px solid ${colors.grey[200]}`,
                },
                "@media screen and (max-width: 47.9375em)": {
                    height: 32,
                    px: 2,
                },
            }}
            wrapperProps={{
                flex: "0 0 auto",
                "@media screen and (max-width: 34.375em)": {
                    display: "none",
                },
            }}
        />
    );
};

// The topbar title slot: report name with the date range picker beside it.
const TopBarTitle = ({ dates, setDates }) => {
    const { t } = useTranslation();
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Subheading>{t("never_sold_products")}</Subheading>
            <TopBarFilter dates={dates} setDates={setDates} />
        </Box>
    );
};

// The phone version of the filter: a full width bar pinned right under the topbar, like daytotals. The page
// body compensates for its height with extra paddingTop at the same breakpoint.
const MobileFilter = ({ dates, setDates }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    return (
        <Box
            sx={{
                display: "none",
                position: "fixed",
                top: "72px",
                left: "0px",
                width: 1,
                px: 3,
                pb: 2,
                background: colors.bg["tertiary"],
                zIndex: 100,
                "@media screen and (max-width: 34.375em)": {
                    display: "block",
                },
            }}
        >
            <ShDateRangePicker
                startDate={dates.startDate}
                endDate={dates.endDate}
                darker
                size={32}
                onClose={(v) => setDates({ startDate: v[0].formattedStartDate, endDate: v[0].formattedEndDate })}
                sx={{
                    width: 1,
                    background: `${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200]}`,
                    border: `2px solid ${"transparent"}`,
                    "&:hover": {
                        background: `${theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[100]}`,
                        border: `2px solid ${colors.grey[200]}`,
                    },
                }}
                wrapperProps={{
                    width: 1,
                }}
            />
        </Box>
    );
};

const NeverSoldReport = () => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { showExtraTitle, clearExtraTitle } = useTopbar();

    const now = new Date();
    const [dates, setDates] = useState({
        startDate: format(startOfMonth(now), "yyyy-MM-dd"),
        endDate: format(endOfMonth(now), "yyyy-MM-dd"),
    });
    // Active card tile filter: { staleDays } | { noStock:true } | null.
    const [filter, setFilter] = useState(null);
    const config = useMemo(() => neverSoldConfig(), []);

    useEffect(() => {
        showExtraTitle(<TopBarTitle dates={dates} setDates={setDates} />);
        return () => clearExtraTitle();
    }, [showExtraTitle, clearExtraTitle, dates, t, i18n.language]);

    return (
        <Box sx={{ width: 1, paddingTop: 9,background:"", }}>
            <MobileFilter dates={dates} setDates={setDates} />
            <TopRow dates={dates} filter={filter} setFilter={setFilter} />
            <TabView3
                tabConfig={config}
                sx={{
                    width: 1,
                    minHeight: "calc(100% - 431px)",
                    background: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                    position: "relative",
                    zIndex: 2,
                    display: "flex",
                    flexDirection: "column",
                    "&:before": {
                        content: "''",
                        display: "block",
                        width: "24px",
                        height: "48px",
                        position: "absolute",
                        left: 0,
                        top: "40px",
                        zIndex: 1,
                        backgroundColor: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                        borderTopLeftRadius: "24px",
                        boxShadow: `0 -24px 0 0 ${colors.bg["tertiary"]}`,
                        transition: "250ms",
                    },
                    "&:after": {
                        content: "''",
                        display: "block",
                        width: "24px",
                        height: "48px",
                        position: "absolute",
                        top: "40px",
                        right: 0,
                        zIndex: 1,
                        backgroundColor: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                        borderTopLeftRadius: "24px",
                        boxShadow: `0 -24px 0 0 ${colors.bg["tertiary"]}`,
                        transform: "scaleX(-1)",
                        transition: "250ms",
                    },
                    "@media screen and (max-width: 47.9375em)": {
                        "&:after, &:before": { top: "-0px" },
                    },
                }}
                title={t("never_sold_products")}
            >
                <>
                    <NeverSold config={config.neversold} dates={dates} filter={filter} />
                </>
            </TabView3>
        </Box>
    );
};

export default NeverSoldReport;
